import { Injectable, computed, signal } from '@angular/core';
import { readUserIdFromAccessToken } from './jwt-payload.util';

export type AdminSession = {
  accessToken: string;
  refreshToken: string;
  /** Platform operator id from JWT `id` claim (used for refresh). */
  userId: string;
  /** Operator email captured at login; absent for sessions stored before this field existed. */
  email?: string;
};

const STORAGE_KEY = 'wasel_admin_session_v1';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _session = signal<AdminSession | null>(this.readSession());

  readonly session = computed(() => this._session());
  readonly isAuthenticated = computed(() => !!this._session()?.accessToken);
  readonly email = computed(() => this._session()?.email ?? null);

  constructor() {
    // Another tab refreshed (the backend rotates the pair and honours only the latest access token) or signed out:
    // adopt what it stored so this tab neither sends a dead token nor refreshes with a revoked one.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY || e.key === null) this.reloadFromStorage();
      });
    }
  }

  /** Re-reads the stored session (validated as at startup); null when it was removed or is unusable. */
  reloadFromStorage(): void {
    this._session.set(this.readSession());
  }

  setSession(tokens: Pick<AdminSession, 'accessToken' | 'refreshToken'>, email?: string) {
    const userId = readUserIdFromAccessToken(tokens.accessToken);
    if (!userId) {
      return;
    }
    const normalizedEmail = email?.trim().toLowerCase() || this._session()?.email;
    const session: AdminSession = normalizedEmail
      ? { ...tokens, userId, email: normalizedEmail }
      : { ...tokens, userId };
    this._session.set(session);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }

  clear() {
    this._session.set(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  getAccessToken(): string | null {
    return this._session()?.accessToken ?? null;
  }

  /** Returns refresh body fields when both tokens and `userId` are present. */
  getRefreshContext(): { userId: string; refreshToken: string } | null {
    const s = this._session();
    if (!s?.refreshToken || !s.userId) {
      return null;
    }
    return { userId: s.userId, refreshToken: s.refreshToken };
  }

  private readSession(): AdminSession | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<AdminSession>;
      if (!parsed.accessToken || !parsed.refreshToken) return null;
      const userId =
        typeof parsed.userId === 'string' && parsed.userId.length > 0
          ? parsed.userId
          : readUserIdFromAccessToken(parsed.accessToken);
      if (!userId) return null;
      const email = typeof parsed.email === 'string' && parsed.email.length > 0 ? parsed.email : undefined;
      return email
        ? { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken, userId, email }
        : { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken, userId };
    } catch {
      return null;
    }
  }
}

